import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { downloadCsv } from "../domain/csv";
import {
  eventAudienceLabel,
  eventHiddenFromFamilies,
  formatCheckInAt,
  gradeLabel,
  staff,
  todayIso,
  type SessionMark,
} from "../domain/data";
import { useEvents } from "../state/events";
import { orderedWaitlist, rosterForAttendance } from "../domain/registrations";
import { useStudents } from "../state/students";
import { PrototypeBanner, StaffNav } from "../components/ui";

type Draft = Record<string, SessionMark | "unset">;
type Tab = "roster" | "attendance";

function statusLabel(status: string, checkedInAt: string | null): string {
  if (status === "waitlisted") return "waitlisted";
  if (status === "cancelled") return "cancelled";
  if (checkedInAt) return "checked in";
  return "registered";
}

export function StaffEvent() {
  const { eventId = "fall-activity-day" } = useParams();
  const [params, setParams] = useSearchParams();
  const {
    getEvent,
    registrationsFor,
    countsFor,
    sessionsFor,
    sessionFor,
    saveSession,
    checkInStudent,
    undoCheckIn,
    archiveEvent,
    restoreEvent,
  } = useEvents();
  const { getStudent } = useStudents();
  const event = getEvent(eventId);
  const tab: Tab = params.get("tab") === "attendance" ? "attendance" : "roster";
  const [takingDate, setTakingDate] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [flash, setFlash] = useState("");

  const rows = registrationsFor(eventId);
  const visibleRows = useMemo(
    () => rows.filter((row) => row.status !== "cancelled"),
    [rows],
  );
  const registeredRows = useMemo(
    () => visibleRows.filter((row) => row.status === "registered"),
    [visibleRows],
  );
  const waitlistRows = useMemo(() => orderedWaitlist(rows), [rows]);
  const counts = countsFor(eventId);
  const sessions = sessionsFor(eventId);
  const registeredStudents = useMemo(
    () =>
      rosterForAttendance(rows)
        .map((row) => getStudent(row.studentId))
        .filter((student): student is NonNullable<typeof student> => Boolean(student))
        .sort(
          (a, b) =>
            a.family.localeCompare(b.family) || a.given.localeCompare(b.given),
        ),
    [getStudent, rows],
  );

  if (!event) {
    return (
      <div className="app">
        <PrototypeBanner />
        <main className="shell">
          <p>Event not found.</p>
          <Link to="/staff/events">Back to events</Link>
        </main>
      </div>
    );
  }

  const liftEvent = event;
  const nextWaitlist = waitlistRows[0]
    ? getStudent(waitlistRows[0].studentId)
    : undefined;
  const hidden = eventHiddenFromFamilies(liftEvent);
  const pastByDate = liftEvent.kind === "rsvp" && liftEvent.dateIso < todayIso();
  let presentMarks = 0;
  let markedMarks = 0;
  sessions.forEach((session) => {
    session.rows.forEach((row) => {
      markedMarks += 1;
      if (row.status === "present") presentMarks += 1;
    });
  });
  const rate = markedMarks ? Math.round((presentMarks / markedMarks) * 100) : null;

  function setTab(next: Tab) {
    const copy = new URLSearchParams(params);
    if (next === "attendance") copy.set("tab", "attendance");
    else copy.delete("tab");
    setParams(copy, { replace: true });
    setTakingDate(null);
    setFlash("");
  }

  function startSession(dateIso: string) {
    const saved = sessionFor(liftEvent.id, dateIso);
    const next: Draft = {};
    registeredStudents.forEach((student) => {
      const row = saved?.rows.find((item) => item.studentId === student.id);
      next[student.id] = row?.status ?? "unset";
    });
    setDraft(next);
    setTakingDate(dateIso);
    setFlash("");
    const copy = new URLSearchParams(params);
    copy.set("tab", "attendance");
    setParams(copy, { replace: true });
  }

  function changeDate(dateIso: string) {
    startSession(dateIso);
  }

  function markOne(studentId: string, status: SessionMark) {
    setDraft((current) => ({ ...current, [studentId]: status }));
  }

  function markAll(status: SessionMark) {
    const next: Draft = {};
    registeredStudents.forEach((student) => {
      next[student.id] = status;
    });
    setDraft(next);
  }

  function saveDraft() {
    if (!takingDate) return;
    const savedRows = registeredStudents.flatMap((student) => {
      const status = draft[student.id];
      if (status !== "present" && status !== "absent") return [];
      return [
        {
          studentId: student.id,
          status,
          at: status === "present" ? nowClock() : null,
        },
      ];
    });
    saveSession(liftEvent.id, takingDate, savedRows);
    setTakingDate(null);
    setFlash("Attendance saved.");
  }

  function exportCsv() {
    const extraHeaders = (liftEvent.questions ?? []).map((item) => item.label);
    downloadCsv(
      `${liftEvent.id}-roster.csv`,
      [
        "Student",
        "School",
        "Grade",
        "Email",
        "Guardian",
        "Phone",
        "Status",
        "Check-in",
        ...extraHeaders,
      ],
      [...registeredRows, ...waitlistRows].flatMap((row) => {
        const student = getStudent(row.studentId);
        if (!student) return [];
        return [
          [
            `${student.given} ${student.family}`,
            student.school,
            gradeLabel(student.grade),
            student.email,
            student.guardianName,
            student.guardianPhone,
            statusLabel(row.status, row.checkedInAt),
            row.checkedInAt ?? "",
            ...(liftEvent.questions ?? []).map(
              (item) => row.answers?.[item.id] ?? "",
            ),
          ],
        ];
      }),
    );
  }

  const unsetCount = registeredStudents.filter(
    (student) => (draft[student.id] ?? "unset") === "unset",
  ).length;
  const draftPresent = registeredStudents.filter(
    (student) => draft[student.id] === "present",
  ).length;
  const draftAbsent = registeredStudents.filter(
    (student) => draft[student.id] === "absent",
  ).length;
  const canSave = takingDate && registeredStudents.length > 0 && unsetCount === 0;
  const existingTake = takingDate ? sessionFor(liftEvent.id, takingDate) : undefined;

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <StaffNav />
        <p className="meta">
          Staff · {staff.given} {staff.family} ·{" "}
          {liftEvent.kind === "program" ? "afterschool program" : "event"}
        </p>
        <h1>{event.name}</h1>
        <p className="lede">
          {event.dateLabel} · {event.timeLabel} · {event.location}
          <br />
          {eventAudienceLabel(event)}
          {liftEvent.kind === "program"
            ? " · staff attendance only"
            : hidden
              ? " · hidden from families"
              : ""}
        </p>
        {event.description ? (
          <p className="lede event-description">{event.description}</p>
        ) : null}

        <div className="actions" style={{ marginTop: 0, marginBottom: "1rem" }}>
          {liftEvent.kind === "rsvp" && !hidden ? (
            <Link className="btn ghost" to={`/staff/events/${liftEvent.id}/check-in`}>
              Check in
            </Link>
          ) : null}
          <button type="button" onClick={exportCsv} disabled={visibleRows.length === 0}>
            Export CSV
          </button>
          {liftEvent.kind === "rsvp" ? (
            <Link className="btn ghost" to={`/staff/events/${liftEvent.id}/form`}>
              {hidden ? "View questions" : "Design questions"}
            </Link>
          ) : null}
          {liftEvent.kind === "program" || pastByDate ? null : liftEvent.status ===
            "archived" ? (
            <button
              type="button"
              className="ghost"
              onClick={() => restoreEvent(liftEvent.id)}
            >
              Show to families
            </button>
          ) : (
            <button
              type="button"
              className="ghost"
              onClick={() => archiveEvent(liftEvent.id)}
            >
              Archive now
            </button>
          )}
          <Link className="btn ghost" to="/staff/events">
            All events
          </Link>
        </div>

        <div className="grid-2">
          <div className="wire">
            <div className="meta">Registered</div>
            <strong style={{ fontSize: "1.5rem" }}>{counts.registered}</strong>
          </div>
          {liftEvent.kind === "rsvp" ? (
            <>
              <div className="wire">
                <div className="meta">Checked in</div>
                <strong style={{ fontSize: "1.5rem" }}>{counts.checkedIn}</strong>
              </div>
              <div className="wire">
                <div className="meta">Remaining</div>
                <strong style={{ fontSize: "1.5rem" }}>{counts.remaining}</strong>
              </div>
              <div className="wire dashed">
                <div className="meta">Waitlist</div>
                <strong style={{ fontSize: "1.5rem" }}>{counts.waitlisted}</strong>
                <div className="anno">
                  {nextWaitlist
                    ? `Next: ${nextWaitlist.given} ${nextWaitlist.family}`
                    : "No one waiting"}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="wire">
                <div className="meta">Sessions</div>
                <strong style={{ fontSize: "1.5rem" }}>{sessions.length}</strong>
              </div>
              <div className="wire">
                <div className="meta">Attendance rate</div>
                <strong style={{ fontSize: "1.5rem" }}>
                  {rate === null ? "-" : `${rate}%`}
                </strong>
              </div>
              <div className="wire dashed">
                <div className="meta">Capacity</div>
                <strong style={{ fontSize: "1.5rem" }}>{event.capacity}</strong>
              </div>
            </>
          )}
        </div>

        <div className="event-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "roster"}
            onClick={() => setTab("roster")}
          >
            Roster
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "attendance"}
            onClick={() => setTab("attendance")}
          >
            Attendance
          </button>
        </div>

        {tab === "roster" ? (
          <>
            <h2 style={{ marginTop: 0 }}>Registered students</h2>
            <p className="lede">
              {liftEvent.kind === "program"
                ? "This year's roster for this program. Take attendance on the Attendance tab."
                : counts.remaining > 0
                  ? `${counts.remaining} ${counts.remaining === 1 ? "spot" : "spots"} left. When this trip is full, families can join a waitlist.`
                  : counts.waitlisted > 0
                    ? `Every seat is taken. ${counts.waitlisted} ${counts.waitlisted === 1 ? "family is" : "families are"} waiting. A cancellation registers the first person on the waitlist.`
                    : "Every seat is taken. Families can join a waitlist, and a cancellation registers the first person waiting."}
            </p>
            {registeredRows.length === 0 ? (
              <div className="wire dashed">
                <div className="meta">No student registrations yet</div>
              </div>
            ) : (
              <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>School</th>
                    <th>Status</th>
                    <th>Check-in</th>
                    {(liftEvent.questions ?? []).map((item) => (
                      <th key={item.id}>{item.label}</th>
                    ))}
                    {liftEvent.kind === "rsvp" ? <th></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {registeredRows.map((row) => {
                    const student = getStudent(row.studentId);
                    if (!student) return null;
                    const waiting =
                      row.status === "registered" && !row.checkedInAt;
                    return (
                      <tr key={row.id}>
                        <td>
                          {student.given} {student.family}
                        </td>
                        <td>{student.school}</td>
                        <td>
                          <span className="tag muted">
                            {statusLabel(row.status, row.checkedInAt)}
                          </span>
                        </td>
                        <td>{formatCheckInAt(row.checkedInAt)}</td>
                        {(liftEvent.questions ?? []).map((item) => (
                          <td key={item.id}>{row.answers?.[item.id] || "-"}</td>
                        ))}
                        {liftEvent.kind === "rsvp" ? (
                          <td>
                            {waiting ? (
                              <button
                                type="button"
                                onClick={() =>
                                  checkInStudent(event.id, row.studentId)
                                }
                              >
                                Check in
                              </button>
                            ) : row.checkedInAt ? (
                              <button
                                type="button"
                                className="ghost"
                                onClick={() => undoCheckIn(event.id, row.id)}
                              >
                                Undo
                              </button>
                            ) : null}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
            {liftEvent.kind === "rsvp" ? (
              <>
                <h2>Waitlist</h2>
                <p className="lede">
                  In the order families joined. If a registered student cancels, the first person here is registered and their family is emailed.
                </p>
                {waitlistRows.length === 0 ? (
                  <div className="wire dashed">
                    <div className="meta">No one is waiting</div>
                  </div>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Student</th>
                          <th>School</th>
                          <th>Guardian</th>
                          <th>Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waitlistRows.map((row, index) => {
                          const student = getStudent(row.studentId);
                          if (!student) return null;
                          return (
                            <tr key={row.id}>
                              <td>{index + 1}</td>
                              <td>
                                {student.given} {student.family}
                                {index === 0 ? (
                                  <>
                                    {" "}
                                    <span className="tag">Next</span>
                                  </>
                                ) : null}
                              </td>
                              <td>{student.school}</td>
                              <td>{student.guardianName}</td>
                              <td>{student.guardianPhone}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="anno">
                  Door scans today: {counts.checkedIn} checked in.
                </p>
              </>
            ) : null}
          </>
        ) : takingDate ? (
          <>
            <h2 style={{ marginTop: 0 }}>
              {existingTake ? "Edit attendance" : "Take attendance"}
            </h2>
            <p className="lede">
              {existingTake
                ? `Saved ${existingTake.savedAt}. Change marks and save again.`
                : "Mark each student, then save. Same date updates this session."}
            </p>

            <div className="field" style={{ marginBottom: "1rem" }}>
              <label htmlFor="attend-date">Attendance date</label>
              <input
                id="attend-date"
                type="date"
                value={takingDate}
                onChange={(e) => changeDate(e.target.value)}
              />
            </div>

            <div className="actions" style={{ marginTop: 0, marginBottom: "1rem" }}>
              <button type="button" onClick={() => markAll("present")}>
                All present
              </button>
              <button type="button" className="ghost" onClick={() => markAll("absent")}>
                All absent
              </button>
            </div>

            <div className="grid-2" style={{ marginBottom: "1rem" }}>
              <div className="wire">
                <div className="meta">Present</div>
                <strong style={{ fontSize: "1.5rem" }}>{draftPresent}</strong>
              </div>
              <div className="wire">
                <div className="meta">Absent</div>
                <strong style={{ fontSize: "1.5rem" }}>{draftAbsent}</strong>
              </div>
              <div className="wire dashed">
                <div className="meta">Not marked</div>
                <strong style={{ fontSize: "1.5rem" }}>{unsetCount}</strong>
              </div>
              <div className="wire dashed">
                <div className="meta">Expected</div>
                <strong style={{ fontSize: "1.5rem" }}>
                  {registeredStudents.length}
                </strong>
              </div>
            </div>

            {registeredStudents.length === 0 ? (
              <div className="wire dashed">
                <div className="meta">No registered students to mark</div>
              </div>
            ) : (
              <ul className="attend-list">
                {registeredStudents.map((student) => {
                  const status = draft[student.id] ?? "unset";
                  return (
                    <li key={student.id}>
                      <div>
                        <strong>
                          {student.given} {student.family}
                        </strong>
                        <div className="anno">
                          Grade {gradeLabel(student.grade)} · {student.school}
                        </div>
                      </div>
                      <div className="mark-btns">
                        <button
                          type="button"
                          aria-pressed={status === "present"}
                          onClick={() => markOne(student.id, "present")}
                        >
                          Present
                        </button>
                        <button
                          type="button"
                          aria-pressed={status === "absent"}
                          onClick={() => markOne(student.id, "absent")}
                        >
                          Absent
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="actions">
              <button type="button" disabled={!canSave} onClick={saveDraft}>
                Save attendance
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setTakingDate(null);
                  setFlash("");
                }}
              >
                Back to history
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="actions" style={{ marginTop: 0, marginBottom: "1rem" }}>
              <button type="button" onClick={() => startSession(todayIso())}>
                Take attendance
              </button>
            </div>
            {flash ? (
              <div className="flash flash--success" role="status">
                {flash}
              </div>
            ) : null}
            <h2 style={{ marginTop: 0 }}>Attendance history</h2>
            <p className="lede">
              One session per date. Open a row to edit. Families do not see
              these dates as separate events.
            </p>
            {sessions.length === 0 ? (
              <div className="wire dashed">
                <div className="meta">No attendance sessions yet</div>
                <p style={{ margin: "0.5rem 0 0" }}>
                  Take attendance to start the first date.
                </p>
              </div>
            ) : (
              <ul className="list-plain">
                {sessions.map((session) => {
                  const present = session.rows.filter(
                    (row) => row.status === "present",
                  ).length;
                  const absent = session.rows.filter(
                    (row) => row.status === "absent",
                  ).length;
                  return (
                    <li key={session.id}>
                      <button
                        type="button"
                        className="ghost session-link"
                        onClick={() => startSession(session.dateIso)}
                      >
                        <div className="row">
                          <div>
                            <div className="title">
                              <strong>{session.dateLabel}</strong>
                            </div>
                            <div className="anno">
                              {counts.registered} expected · {present} present ·{" "}
                              {absent} absent
                            </div>
                            <div className="anno">Saved {session.savedAt}</div>
                          </div>
                          <div className="tag">Open</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function nowClock(): string {
  return new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}
